import React, { useContext, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { Button, Icon, Typography } from 'foundations-library/components'
import { SimpleColorType, VerbalColorType } from 'foundations-library/types'
import { getVerbalColors } from 'helpers'
import { ThemeContext } from 'providers'
import { useDropzone, DropzoneOptions } from 'react-dropzone'
import Dialog from '../Dialog/Dialog'
import AnimatedTypography from '../AnimatedTypography/AnimatedTypography'

const simpleColors = ['neutral', 'primary', 'primary2', 'secondary', 'tertiary', 'destructive']

const jss = (theme) => {
    const { colors, typography } = theme['foundations-library']
    const listLink = {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: colors.primary[500]
    }

    return {
        wrapper: {
            width: '100%'
        },
        label: {
            marginBottom: 15,
            display: 'inline-flex',
            gap: 2
        },
        required: {
            color: colors.destructive[500]
        },
        dropArea: {
            width: '100%',
            minHeight: 270,
            padding: '40px 24px',
            borderRadius: 24,
            border: `2px dashed ${colors.neutral[100]}`,
            backgroundColor: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'border-color 120ms ease, background-color 120ms ease',
            '&:focus': {
                outline: 'none'
            },
            '&.drag-active': {
                borderColor: ({ accentBorderColor }) => accentBorderColor,
                backgroundColor: ({ accentSoftColor }) => accentSoftColor
            },
            '&.disabled': {
                cursor: 'not-allowed',
                '&:has(.dropzone-limit-overlay)': {
                    opacity: 1
                }
            }
        },
        dropAreaOverlay: {
            position: 'relative',
            overflow: 'hidden'
        },
        overlay: {
            position: 'absolute',
            inset: 0,
            borderRadius: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1
        },
        loadingOverlay: {
            backgroundColor: '#FFFFFF'
        },
        limitOverlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: '24px'
        },
        limitMessage: {
            ...typography.h6,
            color: '#FFFFFF',
            textAlign: 'center',
            maxWidth: 760
        },
        content: {
            maxWidth: 420,
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column'
        },
        iconWrapper: {
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: colors.neutral[100],
            color: colors.neutral[500],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 20
        },
        title: {
            ...typography.h8,
            color: colors.neutral[700],
            marginBottom: 6
        },
        helperText: {
            ...typography.body2,
            color: colors.neutral[700],
            marginBottom: 20
        },
        files: {
            marginBottom: 16,
            padding: 10,
            borderRadius: 5,
            border: `1px solid ${colors.neutral[400]}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 10
        },
        fileRow: {
            minHeight: 26,
            display: 'flex',
            alignItems: 'center',
            gap: 5
        },
        listLink,
        fileName: {
            textDecoration: 'underline',
            cursor: 'pointer'
        },
        removeButton: {
            minWidth: 70
        },
        dialogMessage: {
            marginBottom: 24,
            color: colors.neutral[700]
        },
        dialogAction: {
            display: 'flex',
            justifyContent: 'center'
        }
    }
}

const useStyles = makeStyles(jss)

const bytesInMegabyte = 1000000
const bytesInKilobyte = 1000

const formatMaxSizeLabel = (size?: number) => {
    if (!size) return ''

    if (size < bytesInMegabyte) {
        const sizeInKilobytes = size / bytesInKilobyte

        return Number.isInteger(sizeInKilobytes)
            ? `${sizeInKilobytes}KB`
            : `${sizeInKilobytes.toFixed(1)}KB`
    }

    const sizeInMegabytes = size / bytesInMegabyte

    return Number.isInteger(sizeInMegabytes)
        ? `${sizeInMegabytes}MB`
        : `${sizeInMegabytes.toFixed(1)}MB`
}

const getFileExtension = (fileName = '') => {
    const parts = fileName.split('.')

    return parts.length > 1 ? `.${parts.pop()?.toLowerCase()}` : ''
}

const fileMatchesAccept = (file: File, accept?: DropzoneOptions['accept']) => {
    if (!accept || (Array.isArray(accept) && accept.length === 0)) return true

    const acceptedValues = Array.isArray(accept) ? accept : [accept]
    const fileType = file.type?.toLowerCase() || ''
    const fileExtension = getFileExtension(file.name || file.filename)

    return acceptedValues.some((acceptedValue) => {
        const normalizedValue = acceptedValue.toLowerCase()

        if (normalizedValue.startsWith('.')) {
            return fileExtension === normalizedValue
        }

        if (normalizedValue.endsWith('/*')) {
            const baseType = normalizedValue.replace('/*', '')

            return fileType.startsWith(`${baseType}/`)
        }

        return fileType === normalizedValue
    })
}

const formatAcceptLabel = (accept?: DropzoneOptions['accept']) => {
    if (!accept || (Array.isArray(accept) && accept.length === 0)) return 'supported file'

    const acceptedValues = Array.isArray(accept) ? accept : [accept]

    return acceptedValues.map((acceptedValue) => {
        const normalizedValue = acceptedValue.toLowerCase()

        if (normalizedValue.startsWith('.')) {
            return normalizedValue.replace('.', '').toUpperCase()
        }

        if (normalizedValue.endsWith('/*')) {
            return normalizedValue.replace('/*', '').toUpperCase()
        }

        const [type, subtype] = normalizedValue.split('/')

        if (!subtype) return type.toUpperCase()

        if (subtype === 'jpeg') return 'JPG'

        return subtype.toUpperCase()
    }).join(', ')
}

const resolveColorTheme = (colors, color: SimpleColorType | VerbalColorType = 'primary') => {
    const verbalColors = getVerbalColors(colors)
    const family = simpleColors.includes(color.split('-')[0]) ? color.split('-')[0] as SimpleColorType : 'primary'

    return {
        accentColor: verbalColors[color] || verbalColors[family] || verbalColors.primary,
        accentSoftColor: verbalColors[`${family}-50`] || verbalColors[family] || verbalColors.primary,
        accentBorderColor: verbalColors[`${family}-400`] || verbalColors[family] || verbalColors.primary,
        family
    }
}

/**
 *
 */
export interface DropzoneType extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
    accept?: DropzoneOptions['accept'];
    buttonText?: string;
    color?: SimpleColorType | VerbalColorType;
    defaultValue?: File[];
    disabled?: boolean;
    helperText?: string;
    label?: string;
    loading?: boolean;
    maxFilesCount?: number;
    maxSize?: number;
    multiple?: boolean;
    onChange?(files: File[]): unknown;
    onClickRemove?(file: File, index: number): unknown;
    onClickFile?(file: File, index: number): unknown;
    onDropRejected?: DropzoneOptions['onDropRejected'];
    required?: boolean;
    title?: string;
    value?: File[];
    loadingFilesIndexes?: number[]
    'data-testid'?: string;
}

/**
 *
 */
const Dropzone = ({
    accept = ['application/pdf', 'image/jpeg', 'image/png'],
    buttonText = 'Attach file',
    className,
    color = 'primary',
    defaultValue = [],
    disabled = false,
    helperText = 'Must be PDF, JPG, or PNG format, 10MB max',
    label,
    loading = false,
    maxFilesCount,
    maxSize = 10000000,
    multiple = true,
    onChange,
    onClickRemove,
    onClickFile,
    onDropRejected,
    required = false,
    title = 'Attach or drop your file here.',
    loadingFilesIndexes = [],
    value,
    'data-testid': dataTestId = 'dropzone',
    ...props
}: DropzoneType): JSX.Element => {
    const themeContext = useContext(ThemeContext)
    const resolvedColors = resolveColorTheme(themeContext?.['foundations-library']?.colors || {}, color)
    const styles = useStyles(resolvedColors)
    const [internalFiles, setInternalFiles] = useState<File[]>(defaultValue)
    const [isMaxSizeDialogOpen, setIsMaxSizeDialogOpen] = useState(false)
    const files = value !== undefined ? value : internalFiles
    const isLimitReached = typeof maxFilesCount === 'number' && maxFilesCount > 0 && files.length >= maxFilesCount
    const isInteractionDisabled = disabled || loading || isLimitReached
    const maxFilesLabel = typeof maxFilesCount === 'number' && maxFilesCount > 0
        ? `${maxFilesCount} file${maxFilesCount === 1 ? '' : 's'} max`
        : ''
    const displayHelperText = maxFilesLabel ? `${helperText}, ${maxFilesLabel}` : helperText

    const updateFiles = (nextFiles: File[]) => {
        if (value === undefined) {
            setInternalFiles(nextFiles)
        }

        onChange?.(nextFiles)
    }

    const onDropAccepted = (acceptedFiles: File[]) => {
        const nextFiles = multiple ? [...files, ...acceptedFiles] : acceptedFiles.slice(0, 1)
        const limitedFiles = typeof maxFilesCount === 'number' && maxFilesCount > 0
            ? nextFiles.slice(0, maxFilesCount)
            : nextFiles

        updateFiles(limitedFiles)
    }

    const removeFile = (fileIndex: number) => {
        onClickRemove?.(files[fileIndex], fileIndex)
        // eslint-disable-next-line @typescript-eslint/naming-convention
        updateFiles(files.filter((_, index) => index !== fileIndex))
    }

    const handleDropRejected: DropzoneOptions['onDropRejected'] = (rejectedFiles, event) => {
        const hasOversizedFile = typeof maxSize === 'number' && rejectedFiles.some((file) => file.size > maxSize)
        const hasUnsupportedFileType = rejectedFiles.some((file) => !fileMatchesAccept(file, accept))

        if (hasOversizedFile || hasUnsupportedFileType) {
            setIsMaxSizeDialogOpen(true)
        }

        onDropRejected?.(rejectedFiles, event)
    }

    const maxSizeLabel = formatMaxSizeLabel(maxSize)
    const acceptLabel = formatAcceptLabel(accept)

    const {
        getInputProps,
        getRootProps,
        isDragActive,
        open
    } = useDropzone({
        accept,
        disabled: isInteractionDisabled,
        maxSize,
        multiple,
        noClick: true,
        onDropAccepted,
        onDropRejected: handleDropRejected
    })

    const rootProps = getRootProps({
        className: `${styles.dropArea} ${styles.dropAreaOverlay} ${isDragActive ? 'drag-active' : ''} ${isInteractionDisabled ? 'disabled' : ''}`,
        onClick: () => {
            if (!isInteractionDisabled) open()
        },
        onKeyDown: (event) => {
            if (!isInteractionDisabled && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault()
                open()
            }
        },
        role: 'button',
        tabIndex: isInteractionDisabled ? -1 : 0
    })

    return <div className={`${styles.wrapper} ${className || ''}`} data-testid={dataTestId} {...props}>
        {label && <Typography variant='body1' className={styles.label}>
            {label}{required && <Typography variant='body1' color='destructive' tag='span'>*</Typography>}
        </Typography>}
        {files.length > 0 && <div className={styles.files} data-testid={`${dataTestId}-files`}>
            {files.map((file, index) => <div
                key={`${file.name || file.filename}-${file.size}-${index}`}
                className={styles.fileRow}
                data-testid={`${dataTestId}-file-${index}`}
            >
                {loadingFilesIndexes?.includes(index)
                    ? <AnimatedTypography color='primary' variant='body4' className={styles.listLink}>Loading...</AnimatedTypography>
                    : <>
                        <Typography
                            variant='body4'
                            className={`${styles.listLink} ${styles.fileName}`}
                            title={file.name || file.filename}
                            onClick={() => onClickFile?.(files[index], index)}
                        >
                            {file.name || file.filename}
                        </Typography>
                        {!disabled && !loading && <Button
                            type='button'
                            color='destructive'
                            variant='outlined'
                            size='small'
                            className={styles.removeButton}
                            onClick={() => removeFile(index)}
                            data-testid={`${dataTestId}-remove-${index}`}
                        >
                            Remove file
                        </Button>}
                    </>
                }
            </div>)}
        </div>}
        <div {...rootProps} data-testid={`${dataTestId}-area`}>
            <input {...getInputProps({ 'data-testid': `${dataTestId}-input` })} />
            {loading && <div className={`${styles.overlay} ${styles.loadingOverlay}`} data-testid={`${dataTestId}-loading`}>
                <Icon
                    name='spinner'
                    size={100}
                    viewBox='0 0 80 80'
                    color='neutral-400'
                    spin
                    data-testid={`${dataTestId}-loading-icon`}
                />
            </div>}
            {isLimitReached && !loading && <div className={`${styles.overlay} ${styles.limitOverlay} dropzone-limit-overlay`} data-testid={`${dataTestId}-limit-reached`}>
                <Typography variant='h6' className={styles.limitMessage}>
                    Maximum file limit reached. To upload a new file, please remove an existing one.
                </Typography>
            </div>}
            <div className={styles.content}>
                <div className={styles.iconWrapper} data-testid={`${dataTestId}-icon-wrapper`}>
                    <Icon name='upload-alt' size={30} color='inherit' data-testid={`${dataTestId}-upload-icon`} />
                </div>
                <Typography variant='h8' className={styles.title}>
                    {title}
                </Typography>
                <Typography variant='body2' className={styles.helperText}>
                    {displayHelperText}
                </Typography>
                <Button
                    type='button'
                    variant='text'
                    color={resolvedColors.family}
                    disabled={isInteractionDisabled}
                    icon={<Icon
                        name='link'
                        size={22}
                        color={resolvedColors.accentColor}
                        className='button__icon button__icon-left'
                        data-testid={`${dataTestId}-attach-icon`}
                    />}
                    style={{ color: resolvedColors.accentColor }}
                    onClick={(event) => {
                        event.stopPropagation()
                        open()
                    }}
                    data-testid={`${dataTestId}-attach-button`}
                >
                    {buttonText}
                </Button>
            </div>
        </div>
        <Dialog
            isOpen={isMaxSizeDialogOpen}
            onClose={() => setIsMaxSizeDialogOpen(false)}
            title='Upload error'
            width={600}
            data-testid={`${dataTestId}-max-size-dialog`}
        >
            <Typography variant='body1' className={styles.dialogMessage}>
                Your file is too large or not in a supported format. Please upload a {acceptLabel} file under {maxSizeLabel} and try again.
            </Typography>
            <div className={styles.dialogAction}>
                <Button
                    type='button'
                    color='neutral'
                    variant='outlined'
                    onClick={() => setIsMaxSizeDialogOpen(false)}
                    data-testid={`${dataTestId}-max-size-dialog-confirm`}
                >
                    OK
                </Button>
            </div>
        </Dialog>
    </div>
}

export default Dropzone
